import { create } from 'zustand'
import type { PlaygroundMessage, ToolCallInfo, TokenUsage } from '@/schemas/playground'
import { agentService } from '@/services/agent'
import { streamSSE } from '@/lib/sse-client'
import type { SSEEvent } from '@/lib/sse-client'
import { useAuthStore } from '@/stores/auth-store'

interface PlaygroundState {
  // Chat messages
  messages: PlaygroundMessage[]
  // Loading state (waiting for any response)
  isLoading: boolean
  // Streaming state (actively receiving tokens)
  isStreaming: boolean
  // AbortController for cancelling stream
  abortController: AbortController | null
  // Error message
  error: string | null
  // Last token usage stats
  lastUsage: TokenUsage | null
  // Last model/provider info
  lastModel: string | null
  lastProvider: string | null

  // Actions
  sendMessage: (
    projectId: string,
    agentId: string,
    message: string
  ) => Promise<void>
  sendMessageStream: (
    projectId: string,
    agentId: string,
    message: string
  ) => Promise<void>
  cancelStream: () => void
  clearHistory: () => void
  reset: () => void
  clearError: () => void
}

const initialState = {
  messages: [] as PlaygroundMessage[],
  isLoading: false,
  isStreaming: false,
  abortController: null as AbortController | null,
  error: null as string | null,
  lastUsage: null as TokenUsage | null,
  lastModel: null as string | null,
  lastProvider: null as string | null,
}

export const usePlaygroundStore = create<PlaygroundState>()((set, get) => ({
  ...initialState,

  sendMessageStream: async (projectId: string, agentId: string, message: string) => {
    const { messages } = get()

    // Add user message immediately
    const userMessage: PlaygroundMessage = {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    }

    // Add placeholder assistant message
    const assistantMessage: PlaygroundMessage = {
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
    }

    const abortController = new AbortController()

    set({
      messages: [...messages, userMessage, assistantMessage],
      isLoading: true,
      isStreaming: true,
      abortController,
      error: null,
    })

    // Build history (all messages before the user message we just added)
    const history = messages.map((m) => ({
      role: m.role,
      content: m.content,
    }))

    const token = useAuthStore.getState().accessToken
    if (!token) {
      set({ error: 'Nao autenticado', isLoading: false, isStreaming: false, abortController: null })
      return
    }

    const handleEvent = (event: SSEEvent) => {
      switch (event.type) {
        case 'start':
          set({ lastModel: event.data.model, lastProvider: event.data.provider })
          break

        case 'token':
          set((state) => {
            const msgs = [...state.messages]
            const last = msgs[msgs.length - 1]
            if (last && last.role === 'assistant') {
              msgs[msgs.length - 1] = { ...last, content: last.content + event.data.content }
            }
            return { messages: msgs, isLoading: false }
          })
          break

        case 'tool_call_start': {
          const toolCall: ToolCallInfo = {
            name: event.data.name,
            status: 'running',
          }
          set((state) => {
            const msgs = [...state.messages]
            const last = msgs[msgs.length - 1]
            if (last && last.role === 'assistant') {
              const existing = last.toolCalls ?? []
              msgs[msgs.length - 1] = { ...last, toolCalls: [...existing, toolCall] }
            }
            return { messages: msgs }
          })
          break
        }

        case 'tool_call_end':
          set((state) => {
            const msgs = [...state.messages]
            const last = msgs[msgs.length - 1]
            if (last && last.role === 'assistant' && last.toolCalls) {
              const toolCalls = last.toolCalls.map((tc) =>
                tc.name === event.data.name && tc.status === 'running'
                  ? {
                      ...tc,
                      status: (event.data.success ? 'completed' : 'failed') as ToolCallInfo['status'],
                      execution_time_ms: event.data.execution_time_ms,
                    }
                  : tc
              )
              msgs[msgs.length - 1] = { ...last, toolCalls }
            }
            return { messages: msgs }
          })
          break

        case 'usage':
          set({
            lastUsage: {
              prompt_tokens: event.data.prompt_tokens,
              completion_tokens: event.data.completion_tokens,
              total_tokens: event.data.total_tokens,
            },
          })
          break

        case 'end':
          set({ isStreaming: false, isLoading: false, abortController: null })
          break

        case 'error':
          set({
            error: event.data.message,
            isStreaming: false,
            isLoading: false,
            abortController: null,
          })
          break
      }
    }

    await streamSSE({
      url: `/api/v1/projects/${projectId}/agents/${agentId}/playground/stream`,
      body: { message, history },
      token,
      signal: abortController.signal,
      onEvent: handleEvent,
      onError: (err) => {
        set({
          error: err.message || 'Erro ao enviar mensagem',
          isStreaming: false,
          isLoading: false,
          abortController: null,
        })
      },
      onComplete: () => {
        // Ensure state is reset even if no 'end' event was received
        const { isStreaming } = get()
        if (isStreaming) {
          set({ isStreaming: false, isLoading: false, abortController: null })
        }
      },
    })
  },

  cancelStream: () => {
    const { abortController } = get()
    if (abortController) {
      abortController.abort()
      set({ isStreaming: false, isLoading: false, abortController: null })
    }
  },

  sendMessage: async (projectId: string, agentId: string, message: string) => {
    const { messages } = get()

    // Add user message immediately
    const userMessage: PlaygroundMessage = {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    }

    set({
      messages: [...messages, userMessage],
      isLoading: true,
      error: null,
    })

    try {
      // Get updated messages list with user message
      const currentMessages = get().messages

      // Call API with history (exclude the message we just added since it's in the request)
      const history = currentMessages.slice(0, -1)

      const response = await agentService.testAgent(projectId, agentId, {
        message,
        history,
      })

      // Add assistant message
      set((state) => ({
        messages: [...state.messages, response.message],
        lastUsage: response.usage,
        lastModel: response.model,
        lastProvider: response.provider,
        isLoading: false,
      }))
    } catch (error) {
      // Extract error message
      let errorMessage = 'Erro ao enviar mensagem'
      if (error instanceof Error) {
        errorMessage = error.message
      }
      // Check for Axios error response
      if (
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        typeof (error as { response?: { data?: { detail?: string } } }).response
          ?.data?.detail === 'string'
      ) {
        errorMessage = (
          error as { response: { data: { detail: string } } }
        ).response.data.detail
      }

      set({
        error: errorMessage,
        isLoading: false,
      })
    }
  },

  clearHistory: () => {
    const { abortController } = get()
    if (abortController) abortController.abort()
    set({
      messages: [],
      isStreaming: false,
      isLoading: false,
      abortController: null,
      lastUsage: null,
      lastModel: null,
      lastProvider: null,
      error: null,
    })
  },

  reset: () => {
    const { abortController } = get()
    if (abortController) abortController.abort()
    set(initialState)
  },

  clearError: () => {
    set({ error: null })
  },
}))
